/** The fields needed to describe what was actually lifted. */
export interface LiftLoad {
  enteredWeightLb: number;
  reps: number;
  inputWeightMultiplier?: number;
  /** Fraction of body weight in the load; > 0 means a bodyweight lift. */
  bodyWeightFactor?: number;
}

/**
 * How the load is phrased for one lift, without the reps.
 *
 * Bodyweight lifts report the added weight (or none), dumbbell lifts make clear
 * the figure is per dumbbell, and everything else is just the bar weight. A bare
 * "0 lb" for a bodyweight set would be nonsense, which is the whole reason this
 * exists rather than interpolating enteredWeightLb directly.
 */
export function formatLoad(lift: LiftLoad): string {
  const weight = Math.round(lift.enteredWeightLb);
  if ((lift.bodyWeightFactor ?? 0) > 0) {
    return weight > 0 ? `Bodyweight +${weight} lb` : 'Bodyweight';
  }
  if ((lift.inputWeightMultiplier ?? 1) > 1) return `${weight} lb each`;
  return `${weight} lb`;
}

/**
 * The load and reps together, e.g. "225 lb × 3", "70 lb each × 8",
 * "Bodyweight +45 lb × 5".
 *
 * Weight first, then reps: "3 × 225" reads as three sets in gym shorthand, which
 * is not what a logged lift is - one set of `reps` at that weight.
 */
export function formatLoadAndReps(lift: LiftLoad): string {
  return `${formatLoad(lift)} × ${lift.reps}`;
}

/** The exercise name plus its load, e.g. "Barbell Bench Press · 225 lb × 3". */
export function describeLift(exerciseName: string, lift: LiftLoad): string {
  return `${exerciseName} · ${formatLoadAndReps(lift)}`;
}
